import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import DynamicsBCIcon from 'ext-ui/svg/icon/DynamicsBCIcon'
import SageIntacctIcon from 'ext-ui/svg/icon/SageIntacctIcon'
import XeroIcon from 'ext-ui/svg/icon/XeroIcon'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectIntegrationsModalOpen,
  selectTopLevelOrgIntegrationsError,
} from 'src/app/OrganizationIntegrations/redux/organizationIntegrations.selectors'
import RelinkOrgIntegration from 'src/app/RelinkOrgIntegration/RelinkOrgIntegration'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { UnableToConnectToNetsuiteToastMessage } from 'src/app/Toast/Toast'
import Amex from 'src/assets/images/Amex.svg'
import QuickBooks from 'src/assets/images/QuickBooks.svg'
import NetSuite from 'src/assets/svgs/NetSuiteIcon.svg'
import DisconnectBankFeedPopup from 'src/components/DisconnectBankFeedPopup/DisconnectBankFeedPopup'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OrgIntegrationBox from 'src/components/OrgIntegrationBox/OrgIntegrationBox'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { Environments } from 'src/configurations'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
  SupportedBrands,
} from 'src/enums'
import { useBackgroundDispatch, useCookieState, usePrevious } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { getPlatformName } from 'src/models/organizationIntegration.models'
import { UserFeature } from 'src/models/user.models'
import { AriaProps, connectedReplace, LinkTypeProps } from 'src/navigation/Lnk'
import {
  createOrgIntegrationUrl,
  manageOrgIntegrationUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  useQueryOrgIntegrations,
  useReauthOrgIntegration,
} from 'src/queries/organizations/integrations.queries'
import { usePutOrgPhysicalClearingsEnabled } from 'src/queries/organizations/physicalClearings.queries'
import { createSetIsCreateOrgIntegrationOpen } from 'src/redux/actions/app.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectBmoIntegrationsWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectAppName,
  selectBrandFeaturesFromState,
  selectBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export type OrgIntegration = OrgIntegrationShared &
  Partial<LinkTypeProps> &
  AriaProps

interface OrgIntegrationShared {
  title: string
  subtitle: string
  status: OrganizationIntegrationStatus | undefined
  iconSrc?: string
  iconElement?: React.ReactNode
  connectedPlatform: OrganizationConnectedPlatform
  feature?: UserFeature
  organizationId: string
  showBetaTag?: boolean
}

const OrganizationIntegrations: React.FC = () => {
  const translate = useTranslate()

  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const {
    data: orgIntegrations,
    isFetched,
    refetch: refetchOrgIntegrations,
  } = useQueryOrgIntegrations({ orgId: organizationId })

  const topLevelError = useSelector(selectTopLevelOrgIntegrationsError)
  const integrationsModalOpen = useSelector(selectIntegrationsModalOpen)

  const appName = useSelector(selectAppName)
  const brandName = useSelector(selectBrandName)
  const env = useSelector(selectEnvironmentFromState)
  const isProd = env === Environments.Prod

  const bmoIntegrationsWhitelist = useSelector(
    selectBmoIntegrationsWhitelistFromRemoteConfigState,
  )
  const [disconnectPopupOpen, setDisconnectPopupOpen] = useState(false)
  const [relinkLinkUrl, setRelinkLinkUrl] = useState<string | null>(null)
  const { VIEW_AMEX_CPG_INTEGRATION: viewAmexCPGIntegration } =
    useQueryUserFeatures([UserFeature.VIEW_AMEX_CPG_INTEGRATION]).data || {}
  const showCPGTile = useSelector(selectFeatureFlaggerFromState)?.show_cpg_tile

  const {
    mutate: putOrgPhysicalClearingsEnabled,
    isPending: putOrgPhysicalClearingsEnabledLoading,
  } = usePutOrgPhysicalClearingsEnabled(organizationId)

  const { mutate: reauthOrgIntegration } = useReauthOrgIntegration()

  const { integrationsEnabled: brandIntegrationsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )
  const integrationsEnabled =
    brandName !== SupportedBrands.Bmo ||
    bmoIntegrationsWhitelist?.includes(organizationId)

  const orgCantConnectNetsuite = Object.keys(orgIntegrations || {}).some(
    (key) =>
      [
        OrganizationConnectedPlatform.QUICKBOOKS_ONLINE,
        OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
      ].includes(key as OrganizationConnectedPlatform),
  )

  const otherPlatformIsPending = (
    integrationType: OrganizationConnectedPlatform,
  ): boolean => {
    const platforms = Object.values(OrganizationConnectedPlatform)
    const targetIndex = platforms.indexOf(integrationType)
    platforms.splice(targetIndex, 1)
    return platforms.reduce(
      (acc: boolean, curr: OrganizationConnectedPlatform) => {
        const statusForInt = statusForIntegration(curr)
        if (
          statusForInt !== undefined &&
          [
            OrganizationIntegrationStatus.WAITING_FOR_INITIAL_PULL,
            OrganizationIntegrationStatus.WAITING_TO_SYNC,
            OrganizationIntegrationStatus.PENDING_CONFIGURATION,
          ].includes(statusForInt)
        ) {
          return true
        }
        return acc
      },
      false,
    )
  }

  const isDisabledForIntegration = (
    integrationType: OrganizationConnectedPlatform,
  ): boolean => {
    const orgIntegrationTypes = (
      Object.keys(orgIntegrations || {}) as OrganizationConnectedPlatform[]
    ).filter(
      (platform) =>
        orgIntegrations?.[platform]?.connectionStatus !==
          OrganizationIntegrationStatus.UNLINKED &&
        orgIntegrations?.[platform]?.connectionStatus !==
          OrganizationIntegrationStatus.DEAUTHORIZED,
    )
    switch (integrationType) {
      case OrganizationConnectedPlatform.NETSUITE:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.NETSUITE,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.QUICKBOOKS_ONLINE,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.XERO:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.XERO,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.SAGE_INTACCT:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.SAGE_INTACCT,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.DYNAMICS_BC:
        return (
          ((orgIntegrationTypes.length ?? 0) > 0 &&
            !orgIntegrationTypes.includes(
              OrganizationConnectedPlatform.DYNAMICS_BC,
            )) ||
          otherPlatformIsPending(integrationType)
        )
      case OrganizationConnectedPlatform.AMEX:
        return putOrgPhysicalClearingsEnabledLoading
      default:
        return true
    }
  }

  const isLoadingForIntegration = (
    integrationType: OrganizationConnectedPlatform,
  ): boolean => {
    switch (integrationType) {
      case OrganizationConnectedPlatform.AMEX:
        return putOrgPhysicalClearingsEnabledLoading
      default:
        return false
    }
  }

  const statusForIntegration = (
    integrationType: OrganizationConnectedPlatform,
  ): OrganizationIntegrationStatus | undefined => {
    if (!orgIntegrations) {
      return undefined
    }
    const integration = orgIntegrations[integrationType]
    return integration !== undefined ? integration.connectionStatus : undefined
  }

  const urlForIntegration = (
    integrationType: OrganizationConnectedPlatform,
  ): string | undefined => {
    if (!orgIntegrations) {
      return undefined
    }
    const integration = orgIntegrations[integrationType]
    const integrationStatus = statusForIntegration(integrationType)
    if (
      integrationStatus === OrganizationIntegrationStatus.LACK_PERMISSIONS ||
      integrationStatus === OrganizationIntegrationStatus.UNLINKED ||
      integrationStatus === OrganizationIntegrationStatus.DEAUTHORIZED
    ) {
      return undefined
    }
    return integration !== undefined
      ? manageOrgIntegrationUrl(integration.organizationId, integration.id)
      : createOrgIntegrationUrl(organizationId)
  }

  const dispatch = useBackgroundDispatch()

  const handleIntegrationClick = (
    integrationType: OrganizationConnectedPlatform,
  ): void => {
    const integration = orgIntegrations?.[integrationType]
    const integrationStatus = statusForIntegration(integrationType)

    if (integrationStatus === OrganizationIntegrationStatus.LACK_PERMISSIONS) {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <UnableToConnectToNetsuiteToastMessage />,
          failureColorScheme: true,
          hideImage: true,
        }),
      )
    } else if (
      (integrationStatus === OrganizationIntegrationStatus.UNLINKED ||
        integrationStatus === OrganizationIntegrationStatus.DEAUTHORIZED) &&
      integration !== undefined
    ) {
      reauthOrgIntegration(
        {
          orgId: integration.organizationId,
          connectionId: integration.id,
          body: {},
        },
        {
          onSuccess: (data) => {
            setRelinkLinkUrl(data.linkUrl)
          },
          onError: () => {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <span>
                    {translate(
                      'Unable to re-link integration. Please try again.',
                    )}
                  </span>
                ),
                failureColorScheme: true,
                hideImage: true,
              }),
            )
          },
        },
      )
    } else {
      if (integration !== undefined) {
        dispatch(
          connectedReplace(
            manageOrgIntegrationUrl(integration.organizationId, integration.id),
          ),
        )
      } else {
        dispatch(createSetIsCreateOrgIntegrationOpen(true, integrationType))
      }
    }
  }

  // Whats New Cookie
  const { updateCookieValue: setShouldOpenWhatsNew } = useCookieState(
    CookieNames.App,
    CookieValuePaths.shouldShowRulesWhatsNew,
    true,
  )

  const handleAmexBankFeedClick = (): void => {
    if (organization === undefined) {
      return
    }
    if (organization.physicalClearingsEnabled) {
      setDisconnectPopupOpen(true)
    } else {
      putOrgPhysicalClearingsEnabled({
        id: organization.id,
        body: {
          enabled: !organization.physicalClearingsEnabled,
        },
      })
    }
    // adjust whats new cookie to prevent the CPG whats new from showing in orgs who turn it off
    setShouldOpenWhatsNew(false)
  }

  const handleCancelDisconectFromAmexBankFeedClick = (): void => {
    setDisconnectPopupOpen(false)
  }

  // listen for when modals close to check for updates to status

  const prevIntegrationsModalOpen = usePrevious(integrationsModalOpen)

  useEffect(() => {
    const justClosed =
      prevIntegrationsModalOpen === true && integrationsModalOpen === false
    if (justClosed) {
      refetchOrgIntegrations()
    }
  }, [integrationsModalOpen, prevIntegrationsModalOpen])

  const integrations: OrgIntegration[] = useMemo(() => {
    const integrationsLocal: OrgIntegration[] = []
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.NETSUITE),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        {
          integration: getPlatformName(OrganizationConnectedPlatform.NETSUITE),
        },
      ),
      status: statusForIntegration(OrganizationConnectedPlatform.NETSUITE),
      iconSrc: NetSuite,
      to: urlForIntegration(OrganizationConnectedPlatform.NETSUITE),
      ariaLabel: getPlatformName(OrganizationConnectedPlatform.NETSUITE),
      connectedPlatform: OrganizationConnectedPlatform.NETSUITE,
      feature: UserFeature.NETSUITE_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(OrganizationConnectedPlatform.NETSUITE)
      },
    })
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.DYNAMICS_BC),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        {
          integration: getPlatformName(
            OrganizationConnectedPlatform.DYNAMICS_BC,
          ),
        },
      ),
      status: statusForIntegration(OrganizationConnectedPlatform.DYNAMICS_BC),
      iconElement: <DynamicsBCIcon width={48} height={48} />,
      to: urlForIntegration(OrganizationConnectedPlatform.DYNAMICS_BC),
      ariaLabel: getPlatformName(OrganizationConnectedPlatform.DYNAMICS_BC),
      connectedPlatform: OrganizationConnectedPlatform.DYNAMICS_BC,
      feature: UserFeature.DYNAMICS_BC_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(OrganizationConnectedPlatform.DYNAMICS_BC)
      },
      showBetaTag: true,
    })
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        {
          integration: getPlatformName(
            OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
          ),
        },
      ),
      status: statusForIntegration(
        OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
      ),
      iconSrc: QuickBooks,
      to: urlForIntegration(OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP),
      ariaLabel: getPlatformName(
        OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
      ),
      connectedPlatform: OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
      feature: UserFeature.QUICKBOOKS_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP)
      },
      showBetaTag: true,
    })
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.QUICKBOOKS_ONLINE),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        {
          integration: getPlatformName(
            OrganizationConnectedPlatform.QUICKBOOKS_ONLINE,
          ),
        },
      ),
      status: statusForIntegration(
        isProd
          ? OrganizationConnectedPlatform.QUICKBOOKS_ONLINE
          : OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
      ),
      iconSrc: QuickBooks,
      to: urlForIntegration(
        isProd
          ? OrganizationConnectedPlatform.QUICKBOOKS_ONLINE
          : OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
      ),
      ariaLabel: getPlatformName(
        OrganizationConnectedPlatform.QUICKBOOKS_ONLINE,
      ),
      connectedPlatform: isProd
        ? OrganizationConnectedPlatform.QUICKBOOKS_ONLINE
        : OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
      feature: UserFeature.QUICKBOOKS_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(
          isProd
            ? OrganizationConnectedPlatform.QUICKBOOKS_ONLINE
            : OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
        )
      },
    })
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.XERO),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        { integration: getPlatformName(OrganizationConnectedPlatform.XERO) },
      ),
      status: statusForIntegration(OrganizationConnectedPlatform.XERO),
      iconElement: <XeroIcon width={48} height={48} />,
      to: urlForIntegration(OrganizationConnectedPlatform.XERO),
      ariaLabel: getPlatformName(OrganizationConnectedPlatform.XERO),
      connectedPlatform: OrganizationConnectedPlatform.XERO,
      feature: UserFeature.XERO_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(OrganizationConnectedPlatform.XERO)
      },
      showBetaTag: true,
    })
    integrationsLocal.push({
      title: getPlatformName(OrganizationConnectedPlatform.SAGE_INTACCT),
      subtitle: translate(
        'Push cleared transactions to ${integration} for seamless, data-rich reconciliation.',
        {
          integration: getPlatformName(
            OrganizationConnectedPlatform.SAGE_INTACCT,
          ),
        },
      ),
      status: statusForIntegration(OrganizationConnectedPlatform.SAGE_INTACCT),
      iconElement: <SageIntacctIcon width={48} height={48} />,
      to: urlForIntegration(OrganizationConnectedPlatform.SAGE_INTACCT),
      ariaLabel: getPlatformName(OrganizationConnectedPlatform.SAGE_INTACCT),
      connectedPlatform: OrganizationConnectedPlatform.SAGE_INTACCT,
      feature: UserFeature.SAGE_INTACCT_INTEGRATION,
      organizationId,
      onClick: (): void => {
        handleIntegrationClick(OrganizationConnectedPlatform.SAGE_INTACCT)
      },
      showBetaTag: true,
    })
    if (viewAmexCPGIntegration && showCPGTile) {
      integrationsLocal.push({
        title: getPlatformName(OrganizationConnectedPlatform.AMEX),
        subtitle: translate(
          'Cleared transactions from your American Express card will import into ${brandName} and appear alongside virtual card transactions for a comprehensive view of card spend.',
          {
            brandName: appName,
          },
        ),

        status:
          organization !== undefined && organization.physicalClearingsEnabled
            ? OrganizationIntegrationStatus.ACTIVE
            : undefined,
        iconSrc: Amex,
        to: urlForIntegration(OrganizationConnectedPlatform.AMEX),
        ariaLabel: getPlatformName(OrganizationConnectedPlatform.AMEX),
        connectedPlatform: OrganizationConnectedPlatform.AMEX,
        organizationId,
        onClick: (): void => {
          handleAmexBankFeedClick()
        },
      })
    }
    return integrationsLocal
  }, [orgIntegrations, organization, viewAmexCPGIntegration, showCPGTile])

  if (topLevelError !== undefined || !brandIntegrationsEnabled) {
    return <NotFoundBackgroundPageContent />
  }

  return (
    <>
      <Helmet>
        <title>{`Organization Integrations | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Integrations page of ${appName}`}
        />
      </Helmet>
      <LayoutShell
        subnavOptions={[]}
        subnavDesign={SubnavDesign.Floating}
        content={
          <>
            {!integrationsEnabled && (
              <div
                css={css`
                  margin-left: 40px;
                  margin-right: 40px;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  <Trns
                    source={
                      'Are you interested in connecting with third-party integrations like QuickBooks and NetSuite to help better manage your workflow? Let us know via our ${resourceCenter}.'
                    }
                    expressions={{
                      resourceCenter: (
                        <ResourceCenterLink
                          underline
                          size={HoverableLinkSizes.Large}
                        />
                      ),
                    }}
                  />
                </span>
              </div>
            )}
            {integrationsEnabled && (
              <div
                css={css`
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                  flex-grow: 1;
                  gap: 20px;
                `}
              >
                {!isFetched || orgCantConnectNetsuite === undefined ? (
                  <LoadingBackgroundPage />
                ) : (
                  integrations.map((integration, i) => (
                    <OrgIntegrationBox
                      key={i}
                      integration={{
                        ...integration,
                        status: isDisabledForIntegration(
                          integration?.connectedPlatform,
                        )
                          ? OrganizationIntegrationStatus.CONNECTED_TO_OTHER_SERVICE
                          : integration?.status,
                      }}
                      loading={isLoadingForIntegration(
                        integration?.connectedPlatform,
                      )}
                    />
                  ))
                )}
              </div>
            )}
            {disconnectPopupOpen && (
              <DisconnectBankFeedPopup
                organizationId={organizationId}
                closePopup={handleCancelDisconectFromAmexBankFeedClick}
              />
            )}
            {relinkLinkUrl !== null && (
              <RelinkOrgIntegration
                linkUrl={relinkLinkUrl}
                onClose={() => setRelinkLinkUrl(null)}
              />
            )}
          </>
        }
        title={<span>{translate('Integrations')}</span>}
        buttons={[]}
      />
    </>
  )
}

export default OrganizationIntegrations
